import { FC } from "react"
import { Loader2, CheckCircle, Circle } from "lucide-react"
import { cn } from "@/lib/utils"
import { DocumentStagesData } from "@/hooks/useDocumentStages"

// Helper component for individual stage visualization - Copied from DocumentCard
const StageIndicator: FC<{
	status: DocumentStagesData["stage1"]["status"]
	label: string
}> = ({ status, label }) => {
	let IconComponent
	let iconColor = "text-muted-foreground"
	let animate = false

	switch (status) {
		case "completed":
			IconComponent = CheckCircle
			iconColor = "text-green-500"
			break
		case "in_progress":
			IconComponent = Loader2
			iconColor = "text-blue-500"
			animate = true
			break
		case "not_started":
		default:
			IconComponent = Circle
			iconColor = "text-gray-300"
			break
	}

	return (
		<div className="flex flex-col items-center">
			<IconComponent
				className={cn("h-5 w-5", iconColor, animate && "animate-spin")}
			/>
			<span className="text-xs text-muted-foreground mt-1 text-center">
				{label}
			</span>
		</div>
	)
}

interface StagesPipelineProps {
	stagesData: DocumentStagesData | undefined | null
	isLoading: boolean
	error: unknown // Consider using a more specific error type if available
	stagesPipelineVisible: boolean
}

export const StagesPipeline: FC<StagesPipelineProps> = ({
	stagesData,
	isLoading,
	error,
	stagesPipelineVisible,
}) => {
	// Determine the label for the Parsed stage
	let parsedLabel = "Parsed"
	if (
		stagesData?.stage1?.status === "in_progress" &&
		stagesData.stage1.progress_index != null &&
		stagesData.stage1.progress_total != null &&
		stagesData.stage1.progress_total > 0
	) {
		// Ensure total is not 0
		parsedLabel = `Parsed (${stagesData.stage1.progress_index}/${stagesData.stage1.progress_total})`
	}

	return (
		<div
			className={cn(
				"pt-3 border-t overflow-hidden transition-all duration-700 ease-in-out",
				stagesPipelineVisible
					? "max-h-24 opacity-100 translate-y-0 mt-2"
					: "max-h-0 opacity-0 translate-y-4"
			)}
		>
			{isLoading && (
				<div className="flex items-center justify-center text-xs text-muted-foreground">
					<Loader2 className="h-4 w-4 mr-2 animate-spin" />
					Loading Stages...
				</div>
			)}
			{error && !String(error).includes("404") && (
				<div className="text-xs text-red-500">Error loading stages</div>
			)}
			{stagesData && !isLoading && !error && (
				<div className="flex items-start justify-between space-x-2">
					<StageIndicator
						status={stagesData.stage1.status}
						label={parsedLabel}
					/>
					<div className="flex-1 h-px mt-[10px] bg-border"></div>
					<StageIndicator status={stagesData.stage2.status} label="Summary" />
					<div className="flex-1 h-px mt-[10px] bg-border"></div>
					<StageIndicator status={stagesData.stage3.status} label="Relevance" />
				</div>
			)}
		</div>
	)
}
